/**
 * Firestore Database Utilities for OilWellX
 * Helper functions for common Firestore operations
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
  DocumentData,
  Timestamp,
  addDoc,
  writeBatch,
  WhereFilterOp
} from 'firebase/firestore';
import { db } from '../firebase/client';

/**
 * 🔒 SECURITY: Validate company access
 * Ensures user can only access data for their assigned company
 * Prevents cross-company data leakage
 */
export function validateTenantAccess(requestedCompanyId: string, user: any): boolean {
  // Allow if no user (will be caught by auth layer)
  if (!user) {
    return false;
  }

  // SuperAdmin can access any company
  if (user.role === 'superadmin' || user.isSuperAdmin === true) {
    return true;
  }

  // Get user's companyId (check all possible field names for backward compat)
  const userCompanyId = user.companyId || user.companyId || user.companyId;

  if (!userCompanyId || userCompanyId === 'default') {
    console.error('❌ User has no valid company assignment:', user.email);
    return false;
  }

  // Block access if company mismatch
  if (requestedCompanyId !== userCompanyId) {
    console.error('🚫 SECURITY VIOLATION: User attempted to access different company');
    console.error('   User company:', userCompanyId);
    console.error('   Requested company:', requestedCompanyId);
    console.error('   User email:', user.email);
    return false;
  }

  return true;
}

/**
 * Get a single document by ID
 */
export async function getDocument<T = DocumentData>(
  collectionName: string,
  documentId: string
): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }
    return null;
  } catch (error) {
    console.error(`Error getting document from ${collectionName}:`, error);
    return null;
  }
}

/**
 * Get all documents from a collection
 */
export async function getAllDocuments<T = DocumentData>(
  collectionName: string
): Promise<T[]> {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as T[];
  } catch (error) {
    console.error(`Error getting documents from ${collectionName}:`, error);
    return [];
  }
}

/**
 * Query documents with filters
 */
export async function queryDocuments<T = DocumentData>(
  collectionName: string,
  filters: Array<{ field: string; operator: WhereFilterOp; value: any }> = [],
  orderByField?: string,
  orderDirection: 'asc' | 'desc' = 'asc',
  limitCount?: number
): Promise<T[]> {
  try {
    const constraints: QueryConstraint[] = [];

    // Add where clauses
    filters.forEach(filter => {
      constraints.push(where(filter.field, filter.operator, filter.value));
    });

    // Add order by
    if (orderByField) {
      constraints.push(orderBy(orderByField, orderDirection));
    }

    // Add limit
    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(collection(db, collectionName), ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as T[];
  } catch (error) {
    console.error(`Error querying ${collectionName}:`, error);
    return [];
  }
}

/**
 * Create a new document with auto-generated ID
 */
export async function createDocument<T = DocumentData>(
  collectionName: string,
  data: Partial<T>
): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error(`Error creating document in ${collectionName}:`, error);
    return null;
  }
}

/**
 * Set a document with specific ID (creates or overwrites)
 */
export async function setDocument<T = DocumentData>(
  collectionName: string,
  documentId: string,
  data: Partial<T>,
  merge: boolean = false
): Promise<boolean> {
  try {
    const docRef = doc(db, collectionName, documentId);
    await setDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now()
    }, { merge });
    return true;
  } catch (error) {
    console.error(`Error setting document in ${collectionName}:`, error);
    return false;
  }
}

/**
 * Update an existing document
 */
export async function updateDocument<T = DocumentData>(
  collectionName: string,
  documentId: string,
  data: Partial<T>
): Promise<boolean> {
  try {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error(`Error updating document in ${collectionName}:`, error);
    return false;
  }
}

/**
 * Delete a document
 */
export async function deleteDocument(
  collectionName: string,
  documentId: string
): Promise<boolean> {
  try {
    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error(`Error deleting document from ${collectionName}:`, error);
    return false;
  }
}

/**
 * Batch write operations
 */
export async function batchWrite(
  operations: Array<{
    type: 'set' | 'update' | 'delete';
    collection: string;
    docId: string;
    data?: any;
  }>
): Promise<boolean> {
  try {
    const batch = writeBatch(db);

    operations.forEach(op => {
      const docRef = doc(db, op.collection, op.docId);

      switch (op.type) {
        case 'set':
          batch.set(docRef, { ...op.data, updatedAt: Timestamp.now() });
          break;
        case 'update':
          batch.update(docRef, { ...op.data, updatedAt: Timestamp.now() });
          break;
        case 'delete':
          batch.delete(docRef);
          break;
      }
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error in batch write:', error);
    return false;
  }
}

/**
 * Get documents by company ID (for multi-tenancy)
 * @deprecated Prefer using company-scoped collection paths: companies/{companyId}/collection
 */
export async function getDocumentsByTenant<T = DocumentData>(
  collectionName: string,
  companyId: string
): Promise<T[]> {
  // Try companyId first, fall back to companyId for backward compat
  return queryDocuments<T>(collectionName, [
    { field: 'companyId', operator: '==', value: companyId }
  ]);
}

/**
 * Recursively remove undefined values from an object (Firestore doesn't allow undefined)
 */
function stripUndefinedValues(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => stripUndefinedValues(item)).filter(item => item !== undefined);
  }

  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = stripUndefinedValues(value);
      }
    }
    return cleaned;
  }

  return obj;
}

/**
 * Create or update a well document in the correct subcollection structure:
 * companies/{companyId}/wells/{wellId}
 */
export async function saveWellDocument(
  companyId: string,
  wellData: any,
  wellId?: string
): Promise<string | null> {

  try {
    if (!companyId) {
      console.error('❌ saveWellDocument: No companyId provided');
      throw new Error('Company ID is required');
    }

    const collectionPath = `companies/${companyId}/wells`;

    const wellsCollection = collection(db, 'companies', companyId, 'wells');

    // 🔧 Strip undefined values - Firestore doesn't allow undefined
    const cleanedWellData = stripUndefinedValues(wellData);

    if (wellId) {
      // Use setDoc with merge: true to handle both Create (with specific ID) and Update
      // This is critical for the "Add Well" flow where we generate an ID for storage
      // BEFORE the document exists in Firestore.
      const wellRef = doc(wellsCollection, wellId);

      // Ensure createdAt is set if missing
      const dataToSave = {
        ...cleanedWellData,
        updatedAt: Timestamp.now()
      };

      if (!dataToSave.createdAt) {
        dataToSave.createdAt = Timestamp.now();
      }

      await setDoc(wellRef, dataToSave, { merge: true });
      return wellId;
    } else {
      // Create new well
      const newWellRef = await addDoc(wellsCollection, {
        ...cleanedWellData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return newWellRef.id;
    }
  } catch (error) {
    console.error('❌ Error saving well document:', error);
    console.error('   Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      code: (error as any)?.code,
      companyId,
      wellId
    });
    return null;
  }
}

/**
 * Get wells for a specific company from subcollection
 * 🔒 SECURITY: Validates tenant access and filters by companyId
 * 
 * @param companyId - Company slug/ID to load wells for
 * @param user - Optional user object for validation (recommended)
 * @returns Array of wells or empty array if access denied
 */
export async function getCompanyWells(companyId: string, user?: any): Promise<any[]> {
  try {
    // 🔒 SECURITY VALIDATION
    if (!companyId || companyId === 'default' || companyId === 'superadmin') {
      console.error('❌ Invalid companyId for well loading:', companyId);
      return [];
    }

    // Validate user has access to this tenant (if user provided)
    if (user && !validateTenantAccess(companyId, user)) {
      console.error('🚫 Access denied: User cannot access wells for tenant:', companyId);
      return [];
    }


    const wellsCollection = collection(db, 'companies', companyId, 'wells');
    const querySnapshot = await getDocs(wellsCollection);

    const wells = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // 🔒 DOUBLE-CHECK: Filter out any wells with wrong companyId
    // (should not happen, but extra safety layer)
    const validWells = wells.filter(well => {
      const wellCompanyId = (well as any).companyId;
      if (wellCompanyId && wellCompanyId !== companyId) {
        return false;
      }
      return true;
    });

    if (validWells.length !== wells.length) {
    }

    return validWells;
  } catch (error) {
    console.error('❌ Error fetching company wells:', error);
    return [];
  }
}

/**
 * Delete a well document completely (Firestore only)
 * NOTE: Storage cleanup must be done separately using deleteStorageFolder()
 * 
 * @param companyId - Company/tenant ID
 * @param wellId - Well document ID to delete
 */
export async function deleteWellDocument(
  companyId: string,
  wellId: string
): Promise<void> {

  try {
    if (!companyId) {
      console.error('❌ deleteWellDocument: No companyId provided');
      throw new Error('Company ID is required');
    }

    if (!wellId) {
      console.error('❌ deleteWellDocument: No wellId provided');
      throw new Error('Well ID is required');
    }

    const wellRef = doc(db, 'companies', companyId, 'wells', wellId);
    await deleteDoc(wellRef);
  } catch (error) {
    console.error('❌ Error deleting well document:', error);
    throw error;
  }
}
